import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider, useLanguage } from "./LanguageContext";
import Home from "./pages/Home";
import PrerenderFallback from "./components/PrerenderFallback";
import CookieBanner from "./components/CookieBanner";

// Code splitting por página: la Home no descarga el código de las internas.
const page = (load) => lazy(() => load().then((m) => { window.__PRERENDER_HTML = ""; return m; }));
const ModulesPage = page(() => import("./pages/ModulesPage"));
const CasesPage = page(() => import("./pages/CasesPage"));
const CaseDetailPage = page(() => import("./pages/CaseDetailPage"));
const LegalPage = page(() => import("./pages/LegalPage"));
const ModulePage = page(() => import("./pages/ModulePage"));
const FAQPage = page(() => import("./pages/FAQPage"));
const GlossaryPage = page(() => import("./pages/GlossaryPage"));
const VeevaPage = page(() => import("./pages/VeevaPage"));
const BlogPage = page(() => import("./pages/BlogPage"));
const BlogPostPage = page(() => import("./pages/BlogPostPage"));
const CountryPage = page(() => import("./pages/CountryPage"));
const ChangelogPage = page(() => import("./pages/ChangelogPage"));
const EventPage = page(() => import("./pages/EventPage"));
const EventStatsPage = page(() => import("./pages/EventStatsPage"));
const DemoPage = page(() => import("./pages/DemoPage"));

// Legacy routes from the old WordPress site (brainmart.com.ar) -> new equivalents.
// Keeps old indexed links working instead of rendering a blank page.
const LEGACY_REDIRECTS = [
  ["/newweb", "/"],
  ["/contacto", "/solicitar-demo"],
  ["/soporte", "/solicitar-demo"],
  ["/es/contactenos.htm", "/solicitar-demo"],
  ["/es/soporte.htm", "/#contact"],
  ["/erp", "/"],
  ["/med", "/"],
  ["/login", "/"],
  ["/wordPress", "/blog"],
  ["/wordpress", "/blog"],
  ["/blog-viejo", "/blog"],
];

// Scrolls to the element matching the URL hash (e.g. /#contact) after client-side
// navigation, or to the top when there is no hash. React Router does not do this.
function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      const t = setTimeout(tryScroll, 80);
      return () => clearTimeout(t);
    }
  }, [pathname, hash]);
  return null;
}

// Las rutas se declaran en ES; /en/... y /pt/... se traducen a la ruta ES equivalente.
function LocalizedRoutes() {
  const location = useLocation();
  const { esPath, lp } = useLanguage();
  return (
            <Suspense fallback={<PrerenderFallback />}>
            <Routes location={{ ...location, pathname: esPath }}>
              <Route path="/" element={<Home />} />
              <Route path="/modulos" element={<ModulesPage />} />
              <Route path="/casos" element={<CasesPage />} />
              <Route path="/casos/:slug" element={<CaseDetailPage />} />
              <Route path="/modulos/:slug" element={<ModulePage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/solicitar-demo" element={<DemoPage />} />
              <Route path="/glosario" element={<GlossaryPage />} />
              <Route path="/veeva" element={<VeevaPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/changelog" element={<ChangelogPage />} />
              <Route path="/evento" element={<EventPage />} />
              <Route path="/evento/estadisticas" element={<EventStatsPage />} />
              <Route path="/evento/:slug" element={<EventPage />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/argentina" element={<CountryPage />} />
              <Route path="/chile" element={<CountryPage />} />
              <Route path="/peru" element={<CountryPage />} />
              <Route path="/colombia" element={<CountryPage />} />
              <Route path="/puerto-rico" element={<CountryPage />} />
              <Route path="/mexico" element={<CountryPage />} />
              {LEGACY_REDIRECTS.map(([from, to]) => (
                <Route key={from} path={from} element={<Navigate to={to} replace />} />
              ))}
              {LEGACY_REDIRECTS.map(([from, to]) => (
                <Route key={`${from}-splat`} path={`${from}/*`} element={<Navigate to={to} replace />} />
              ))}
              <Route path="*" element={<Navigate to={lp("/")} replace />} />
            </Routes>
            </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <LanguageProvider>
            <ScrollToHash />
            <LocalizedRoutes />
            <CookieBanner />
          </LanguageProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
